/* Inside a call.
 *
 * WHAT IS REAL HERE AND WHAT IS NOT. Everything about PEOPLE works: joining,
 * the hand queue, granting the floor, muting, leaving. The audio does not,
 * because the media platform's signalling is the one piece outstanding.
 *
 * That gap is stated on the screen rather than hidden. A room that looks
 * live and carries no sound is worse than one that says it is not connected
 * yet — somebody would sit waiting, then conclude their microphone is
 * broken. The banner goes when the audio does.
 *
 * TWO ROOMS IN ONE SCREEN, and the asymmetry is deliberate. A moderator sees
 * the hand queue and the roster because that is the job. An audience member
 * sees who is speaking and how many are here, and NOT the list: somebody
 * watching a roster count fall reads it as the thing ending and leaves
 * because others left.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Hand,
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
  Users,
  X,
} from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import {
  useJoinRoom,
  useLeaveRoom,
  useEndCall,
  useRaiseHand,
  useRoom,
  useMuteEveryone,
  useSetCallMuted,
  useSetCallRole,
} from '@/api/queries'
import { useCallAudio } from '@/features/church/useCallAudio'
import { ApiError, farewell } from '@/api/client'
import { cn } from '@/lib/utils'

/* The mixer's hard ceiling. Mirrored from the server so the console can
   show the limit rather than discovering it by being refused. */
const MAX_SEATS = 8
import type { AudioState } from '@/features/church/useCallAudio'
import type {
  CallParticipant,
  RoomState,
} from '@/types/api'

export function CallRoomPage() {
  const { callId } = useParams<{ callId: string }>()
  const navigate = useNavigate()

  /* Roster filters live here so a keystroke does not remount the room. */
  const [filters, setFilters] = useState<{ roleId: string | null; q: string }>(
    { roleId: null, q: '' },
  )

  const room = useRoom(callId ?? null, true, filters)
  /* THE ROOM ANSWERING "NOT FOUND" MEANS THE CALL IS OVER — ended, or
     never ours. The last good answer is still cached, so without this the
     microphone stays open into a room the service has already closed. */
  const gone = room.error instanceof ApiError && room.error.status === 404

  /* The microphone is asked for once the room is genuinely ours to be in,
     not on page load — a permission prompt that appears before anybody
     knows why is a prompt people refuse. */
  const audio = useCallAudio(
    callId ?? null,
    Boolean(room.data) && !gone,
    room.data?.you?.leg_id ?? null,
    room.data?.you?.role === 'speaker' && !room.data?.you?.muted,
  )

  /* Counts CONNECTED time, not time since the room opened. A clock that
     starts before the sound does claims a call is running when it is
     still negotiating. */
  /* Beside the stage on a wide screen, so it starts open; over it on a
     phone, where open would hide the call itself, so it starts closed. */
  const [panelOpen, setPanelOpen] = useState(
    () => window.matchMedia?.('(min-width: 768px)').matches ?? true,
  )
  const [confirmEnd, setConfirmEnd] = useState(false)
  const endCall = useEndCall(callId!)
  const [liveSince, setLiveSince] = useState<string | null>(null)
  const liveFor = useElapsed(liveSince)

  /* A few seconds after being given the floor, before saying "you are
     live". Long enough to cover the distribution delay somebody has been
     listening through, short enough not to feel like a fault. */
  const [settling, setSettling] = useState(false)
  const wasSpeaker = useRef(false)
  /* AUDIBLE, NOT SEATED. Everybody holds a seat and arrives muted, so the
     seat says nothing about whether somebody can be heard — the one thing
     every control here turns on. */
  const audible =
    room.data?.you?.role === 'speaker' && !room.data?.you?.muted
  /* ONLY FOR SOMEBODY WHO WAS LISTENING. The hold covers the delay a
     listener has been hearing through; whoever leads the call arrives
     able to speak, has been behind nothing, and was being told to wait
     for no reason. */
  const wasListening = useRef(false)
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const inRoom = Boolean(room.data?.you)
  useEffect(() => {
    if (!inRoom) return
    if (!audible) {
      wasListening.current = true
      wasSpeaker.current = false
      /* Stopped again inside the hold: clear it, or the screen says
         "don't speak yet" to somebody who can no longer speak at all. */
      clearTimeout(settleTimer.current)
      setSettling(false)
      return
    }
    if (wasSpeaker.current) return
    wasSpeaker.current = true
    if (!wasListening.current) return
    setSettling(true)
    clearTimeout(settleTimer.current)
    settleTimer.current = setTimeout(() => setSettling(false), 3000)
  }, [audible, inRoom])
  useEffect(() => () => clearTimeout(settleTimer.current), [])
  useEffect(() => {
    setLiveSince((was) =>
      audio.state === 'live' ? (was ?? new Date().toISOString()) : null,
    )
  }, [audio.state])
  const join = useJoinRoom(callId!)
  const leave = useLeaveRoom(callId!)

  /* Join once on arrival. Opening the room IS joining it — a separate
     "join" button on a screen somebody navigated to on purpose is a step
     that asks them to confirm what they already did. */
  const joined = join.isSuccess || Boolean(room.data?.you)

  /* A REF, NOT `isPending`. Effects run twice in development and the two
     calls are synchronous, so the mutation has not started by the time the
     second one checks — both fire, and two joins racing to open the same
     room is a race the server then has to settle. A ref is set before
     anything async begins, so the second call sees it. */
  const asked = useRef(false)
  useEffect(() => {
    if (callId && !joined && !asked.current) {
      asked.current = true
      join.mutate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId])

  /* SAYING GOODBYE ON THE WAY OUT. Closing the tab, reloading, or going
     back without pressing Leave all used to leave the person in the room
     until the call ended. The server clears anybody gone quiet, but that
     takes a minute and a half; this makes the ordinary case immediate.

     Only once the join has landed: in development the first mount is torn
     down at once, and a goodbye sent before arriving would race the
     arrival. */
  const arrived = useRef(false)
  const leftOnPurpose = useRef(false)
  useEffect(() => {
    if (room.data?.you) arrived.current = true
  }, [room.data?.you])
  useEffect(() => {
    if (!callId) return
    const goodbye = () => {
      if (arrived.current && !leftOnPurpose.current) {
        farewell(`/me/calls/${callId}/leave`)
      }
    }
    window.addEventListener('pagehide', goodbye)
    return () => {
      window.removeEventListener('pagehide', goodbye)
      goodbye()
    }
  }, [callId])

  /* CLEARED BUT STILL HERE. A laptop that slept, or a network that
     dropped for long enough, reads to the server exactly like a closed
     tab. When the screen is plainly still open, walk back in — the
     microphone follows on its own, because the leg went with the seat. */
  useEffect(() => {
    if (
      arrived.current &&
      !leftOnPurpose.current &&
      room.data &&
      !room.data.you &&
      !join.isPending &&
      !leave.isPending
    ) {
      join.mutate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.data?.you, room.data])

  function hangUp() {
    leftOnPurpose.current = true
    leave.mutate(undefined, { onSettled: () => navigate('/') })
  }

  /* The church does not offer this kind of call: the server refuses the
     join, and the room has nothing else to show. */
  if (join.error instanceof ApiError && join.error.code === 'feature_off') {
    return (
      <Card className="p-s8 text-center">
        <p className="text-base text-ink-2">
          Meetings aren't available for your church.
        </p>
        <Button variant="secondary" className="mt-s4" onClick={() => navigate('/')}>
          Back
        </Button>
      </Card>
    )
  }

  if (room.isLoading && !room.data) {
    return (
      <Card className="p-s8 text-center text-ink-2">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
      </Card>
    )
  }

  if (room.isError || !room.data) {
    /* Somebody who was in the room when it ended is told so plainly. */
    const endedUnderUs = gone && room.data
    return (
      <Card className="p-s8 text-center">
        <p className="text-md font-bold">
          {endedUnderUs
            ? `${room.data!.call.title} has ended`
            : 'This call is not available'}
        </p>
        <p className="mt-1 text-base text-ink-2">
          {endedUnderUs
            ? 'Thank you for joining.'
            : 'It may have finished, or it was not for you.'}
        </p>
        <Button variant="secondary" className="mt-s4" onClick={() => navigate('/')}>
          Back
        </Button>
      </Card>
    )
  }

  const state = room.data
  const call = state.call

  return (
    /* A ROOM, NOT A PAGE. Dark and full-bleed, because a call is a place
       you are IN rather than a document you are reading — the surrounding
       chrome competing for attention was most of why it did not feel like
       being in a call. The stage holds whoever can be heard; everyone else
       lives in the panel beside it. */
    <div className="fixed inset-0 z-50 flex flex-col bg-[#1a1a1a] text-[#f1efe8]">
      <header className="flex shrink-0 items-center justify-between gap-s3 border-b border-[#2e2e2e] px-s4 py-s3">
        <div className="flex min-w-0 items-center gap-s2">
          <ConnectionDot state={audio.state} />
          <span className="truncate text-base font-semibold">{call.title}</span>
          {liveFor ? (
            <span className="shrink-0 text-sm tabular-nums text-[#b4b2a9]">
              · {liveFor}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-s3">
          {call.recording ? (
            <span className="rounded-pill bg-[#501313] px-s2 py-0.5 text-xs font-bold text-[#F09595]">
              REC
            </span>
          ) : null}
          <span className="text-sm text-[#b4b2a9]">
            <Users className="mr-1 inline h-3.5 w-3.5" aria-hidden />
            {state.present}
          </span>
        </div>
      </header>

      {audio.blockedPlayback ? (
        /* THE BROWSER HAS THE AUDIO AND WILL NOT PLAY IT. A policy about
           autoplay, not a fault in the call — and invisible without this,
           because it fails silently and looks exactly like nobody
           talking. One click is the whole fix. */
        <button
          type="button"
          onClick={audio.releasePlayback}
          className="shrink-0 bg-[#412402] px-s4 py-s3 text-center text-base font-bold text-[#FAC775] hover:bg-[#533005]"
        >
          Tap to hear the call — your browser is holding the sound
        </button>
      ) : null}

      <div className="relative flex min-h-0 flex-1">
        <main className="min-w-0 flex-1 overflow-y-auto p-s5">
          {/* A HAND GOING UP ON A PHONE. The panel is closed there, so
              without this whoever runs the call never learns somebody is
              waiting — the hand is raised into nothing. */}
          {state.may_moderate && state.hands_total > 0 && !panelOpen ? (
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              className="mx-auto mb-s4 flex items-center gap-s2 rounded-pill bg-[#412402] px-s4 py-s2 text-sm font-semibold text-[#FAC775]"
            >
              <Hand className="h-4 w-4" aria-hidden />
              {state.hands_total === 1
                ? '1 hand up — see who'
                : `${state.hands_total} hands up — see who`}
            </button>
          ) : null}
          <Stage
            speakers={state.speakers}
            present={state.present}
            audio={audio.state}
            you={state.you}
            canGrant={state.may_moderate}
            settling={settling}
            micDenied={audio.micDenied}
            selfMuted={audio.muted}
          />
        </main>

        {panelOpen ? (
          <aside className="absolute inset-0 z-10 overflow-y-auto bg-[#1a1a1a] md:static md:z-auto md:w-[18rem] md:shrink-0 md:border-l md:border-[#2e2e2e]">
            {/* On a phone the panel covers the call, so it needs its own
                way back. */}
            <div className="flex items-center justify-between border-b border-[#2e2e2e] px-s4 py-s3 md:hidden">
              <span className="text-base font-semibold">People</span>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="flex items-center gap-1 rounded-md px-s2 py-1 text-sm text-[#b4b2a9] hover:bg-[#262626]"
              >
                <X className="h-4 w-4" aria-hidden /> Back to the call
              </button>
            </div>
            <RoomPanel
              callId={callId!}
              state={state}
              filters={filters}
              onFilter={setFilters}
            />
          </aside>
        ) : null}
      </div>

      {state.you ? (
        <Toolbar
          callId={callId!}
          you={state.you}
          audio={audio}
          present={state.present}
          panelOpen={panelOpen}
          onTogglePanel={() => setPanelOpen((o) => !o)}
          onLeave={hangUp}
          leaving={leave.isPending}
          canEnd={state.may_moderate}
          handsWaiting={state.may_moderate ? state.hands_total : 0}
          onEnd={() => setConfirmEnd(true)}
          ending={endCall.isPending}
        />
      ) : null}

      <Confirm
        open={confirmEnd}
        onOpenChange={setConfirmEnd}
        title="End this call for everybody?"
        body="Everyone is disconnected, the recording is closed, and the report becomes available. This cannot be undone."
        confirmLabel="End it"
        loading={endCall.isPending}
        onConfirm={() => {
          leftOnPurpose.current = true
          endCall.mutate(undefined, { onSettled: () => navigate('/calls') })
        }}
      />
    </div>
  )
}

/* ----------------------------------------------------------- side panel */
/*
 * EVERYONE WHO IS NOT ON STAGE. Hands first, because a hand is somebody
 * waiting on a decision and the queue is the moderator's actual job. A
 * member sees only who is speaking — the roster and the queue are not
 * theirs, and a member watching a count fall reads it as the thing ending.
 */

function RoomPanel({
  callId,
  state,
  filters,
  onFilter,
}: {
  callId: string
  state: RoomState
  filters: { roleId: string | null; q: string }
  onFilter: (next: { roleId: string | null; q: string }) => void
}) {
  const setRole = useSetCallRole(callId)
  const setMuted = useSetCallMuted(callId)
  const muteAll = useMuteEveryone(callId)
  const mod = state.may_moderate
  const audible = state.speakers.filter((p) => !p.muted).length
  const listeningCount = Math.max(0, state.present - state.speakers.length)

  /* The call names who leads it; whoever arranged it leads by default. */
  const chairId = state.call.moderator_id ?? state.call.created_by_id
  const chairName = state.call.moderator_name ?? state.call.created_by_name

  /* A MEMBER IS NOT SENT THE ROSTER, deliberately — so the lists below
     are empty for them and nobody could be found in them. The call itself
     names who is chairing, and that is public: a room where a member
     cannot tell who is running it is a room where they do not know whose
     decision a silence is. */
  const everyone = [...state.speakers, ...state.hands, ...state.listeners]
  const hosts = everyone.filter(
    (p, i) =>
      (p.is_moderator || p.user_id === chairId) &&
      everyone.findIndex((q) => q.user_id === p.user_id) === i,
  )

  return (
    <div className="flex flex-col">
      {/* ON AIR, AND HOW MANY SEATS ARE LEFT. The floor is finite — the
          mixer has a hard ceiling — and a moderator who discovers that by
          being refused has already made a promise to somebody. Showing
          "3 of 8" makes the limit a fact they can plan around rather than
          a wall they hit. */}
      <div className="flex items-center gap-s2 bg-[#4A1F17] px-s4 py-s2">
        <span className="h-1.5 w-1.5 rounded-pill bg-[#F08A70]" aria-hidden />
        <span className="font-mono text-xs font-bold tracking-widest text-[#F08A70]">
          ON AIR
        </span>
        <span className="ml-auto font-mono text-xs tabular-nums text-[#F08A70]">
          {audible} of {MAX_SEATS}
        </span>
      </div>

      <div className="flex items-center justify-between border-b border-[#2e2e2e] px-s4 py-s3">
        <span className="text-base font-semibold">
          In the room ({state.present})
        </span>
        {mod && state.speakers.length > 1 ? (
          <button
            type="button"
            onClick={() => muteAll.mutate()}
            className="text-xs text-[#b4b2a9] hover:text-[#f1efe8]"
          >
            Mute all
          </button>
        ) : null}
      </div>

      {mod && state.hands.length ? (
        <div className="border-b border-[#2e2e2e] px-s4 py-s3">
          <p className="mb-s2 text-xs font-bold tracking-wide text-[#EF9F27]">
            HANDS UP · {state.hands_total}
          </p>
          {state.hands.map((p, i) => (
            <div key={p.user_id} className="flex items-center gap-s2 py-1.5">
              {/* RANK, because a queue is read as an order rather than a
                  set — and the person at the top is the one owed the
                  floor. */}
              <span className="w-4 shrink-0 font-mono text-xs tabular-nums text-[#888780]">
                {i + 1}
              </span>
              <Avatar name={p.name} src={p.avatar_url} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{p.name}</div>
                <div className="text-xs text-[#888780]">
                  {waited(p.waiting_seconds)}
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  setRole.mutate({ user_id: p.user_id, role: 'speaker' })
                }
                className="shrink-0 rounded-md border border-[#444441] px-s2 py-0.5 text-xs hover:bg-[#262626]"
              >
                Allow
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {/* WHOEVER RUNS THE CALL, PINNED. A room where you cannot tell who
          is chairing it is a room where nobody knows whose decision a
          silence is. They stay at the top whether or not they happen to
          be speaking. */}
      <div className="border-b border-[#2e2e2e] px-s4 py-s3">
        <p className="mb-s2 text-xs font-bold tracking-wide text-[#888780]">
          RUNNING THIS CALL
        </p>
        {hosts.length ? (
          hosts.map((p) => (
            <div key={p.user_id} className="flex items-center gap-s2 py-1.5">
              <Avatar name={p.name} src={p.avatar_url} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{p.name}</div>
                <div className="truncate text-xs text-[#EF9F27]">
                  {p.user_id === chairId ? 'Chairperson' : 'Moderator'}
                </div>
              </div>
              {p.role === 'speaker' && !p.muted ? (
                <Mic className="h-4 w-4 text-[#97C459]" aria-hidden />
              ) : null}
            </div>
          ))
        ) : chairName ? (
          /* Falls back to the name on the call when the roster is not
             ours to see, which is every member's view. */
          <div className="flex items-center gap-s2 py-1.5">
            <Avatar name={chairName} src={null} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm">{chairName}</div>
              <div className="truncate text-xs text-[#EF9F27]">
                Chairperson
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#888780]">Nobody in particular.</p>
        )}
      </div>

      <div className="px-s4 py-s3">
        <p className="mb-s2 text-xs font-bold tracking-wide text-[#888780]">
          SPEAKING · {state.speakers.length}
        </p>
        {state.speakers.length ? (
          state.speakers.map((p) => (
            <div key={p.user_id} className="flex items-center gap-s2 py-1.5">
              <Avatar name={p.name} src={p.avatar_url} size="sm" />
              <span className="min-w-0 flex-1 truncate text-sm">{p.name}</span>
              {mod && p.user_id !== chairId ? (
                <button
                  type="button"
                  aria-label={p.muted ? 'Turn mic on' : 'Turn mic off'}
                  onClick={() =>
                    setMuted.mutate({ user_id: p.user_id, muted: !p.muted })
                  }
                >
                  {p.muted ? (
                    <MicOff className="h-4 w-4 text-[#EF9F27]" aria-hidden />
                  ) : (
                    <Mic className="h-4 w-4 text-[#97C459]" aria-hidden />
                  )}
                </button>
              ) : p.muted ? (
                <MicOff className="h-4 w-4 text-[#EF9F27]" aria-hidden />
              ) : (
                <Mic className="h-4 w-4 text-[#97C459]" aria-hidden />
              )}
              {/* Not for whoever leads the call: the server refuses to take
                  the floor from them, so the button only ever produced an
                  error. */}
              {mod && p.user_id !== chairId ? (
                <button
                  type="button"
                  onClick={() =>
                    setRole.mutate({ user_id: p.user_id, role: 'listener' })
                  }
                  className="shrink-0 text-xs text-[#888780] hover:text-[#f1efe8]"
                >
                  Stop
                </button>
              ) : null}
            </div>
          ))
        ) : (
          <p className="text-sm text-[#888780]">Nobody yet.</p>
        )}
      </div>

      {mod ? (
        <div className="border-t border-[#2e2e2e] px-s4 py-s3">
          {/* THE AUDIENCE IS A NUMBER, NOT A LIST. A roster of fifty
              thousand is not a thing anybody reads, and scrolling one
              implies the floor could hold them. The list below is a
              lookup — reached by searching for somebody — rather than a
              register to browse. */}
          <div className="mb-s2 flex items-baseline gap-s2">
            <span className="text-lg font-extrabold tabular-nums">
              {listeningCount}
            </span>
            <span className="text-xs text-[#888780]">listening</span>
          </div>
          <input
            type="search"
            value={filters.q}
            onChange={(e) => onFilter({ ...filters, q: e.target.value })}
            placeholder="Search by name"
            className="mb-s2 w-full rounded-md border border-[#2e2e2e] bg-[#262626] px-s2 py-1 text-sm outline-none placeholder:text-[#888780] focus:border-[#444441]"
          />
          {state.listeners.map((p) => (
            <div key={p.user_id} className="flex items-center gap-s2 py-1.5">
              <Avatar name={p.name} src={p.avatar_url} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{p.name}</div>
                {p.join_failed ? (
                  /* THE MODERATOR'S LIST ONLY. A listener told that others
                     could not get in concludes the event is broken and
                     leaves — and this person is not coming, so waiting on
                     them is the thing to prevent. */
                  <div className="truncate text-xs text-[#F09595]">
                    could not connect
                  </div>
                ) : p.role_name ? (
                  <RoleBadge name={p.role_name} />
                ) : null}
              </div>
              <button
                type="button"
                onClick={() =>
                  setRole.mutate({ user_id: p.user_id, role: 'speaker' })
                }
                className="shrink-0 text-xs text-[#888780] hover:text-[#f1efe8]"
              >
                Allow
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/* ---------------------------------------------------------------- stage */
/*
 * WHO CAN BE HEARD, and nothing else. A call screen's centre should answer
 * that without being read — a lit tile for whoever holds the floor, a dim
 * one for somebody muted, and a plain count for the audience, who are
 * present but not on stage.
 */

function ConnectionDot({ state }: { state: AudioState }) {
  const live = state === 'live'
  const bad = state === 'unreachable' || state === 'failed'
  return (
    <span
      className={cn(
        'h-2 w-2 shrink-0 rounded-pill',
        live && 'bg-[#97C459]',
        bad && 'bg-[#F09595]',
        !live && !bad && 'animate-pulse bg-[#EF9F27]',
      )}
      aria-label={live ? 'Connected' : bad ? 'Not connected' : 'Connecting'}
    />
  )
}

function Stage({
  speakers,
  present,
  audio,
  you,
  canGrant,
  settling,
  micDenied,
  selfMuted,
}: {
  speakers: CallParticipant[]
  present: number
  audio: AudioState
  you: CallParticipant | null
  canGrant: boolean
  settling: boolean
  micDenied: boolean
  selfMuted: boolean
}) {
  const listening = Math.max(0, present - speakers.length)

  return (
    <div className="mx-auto flex h-full max-w-4xl flex-col justify-center gap-s5">
      <StageNotice
        state={audio}
        you={you}
        settling={settling}
        micDenied={micDenied}
        selfMuted={selfMuted}
      />

      {speakers.length ? (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-s3">
          {speakers.map((p) => (
            <div
              key={p.user_id}
              className={cn(
                'relative flex aspect-[4/3] flex-col items-center justify-center gap-s2 rounded-xl border bg-[#262626] p-s3',
                p.muted
                  ? 'border-[#2e2e2e] opacity-70'
                  : 'border-[#97C459]',
              )}
            >
              <Avatar name={p.name} src={p.avatar_url} size="lg" />
              <div className="max-w-full truncate text-base font-semibold">
                {p.name}
              </div>
              <div
                className={cn(
                  'absolute bottom-s3 left-s3 flex items-center gap-1 text-xs',
                  p.muted ? 'text-[#EF9F27]' : 'text-[#97C459]',
                )}
              >
                {p.muted ? (
                  <>
                    <MicOff className="h-3.5 w-3.5" aria-hidden /> muted
                  </>
                ) : (
                  <>
                    <Mic className="h-3.5 w-3.5" aria-hidden />{' '}
                    {speakerNote(p).toLowerCase()}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-[#2e2e2e] p-s8 text-center">
          <p className="text-lg font-semibold">Nobody has the microphone</p>
          {/* SILENCE HERE IS THE DESIGN, NOT A FAULT. This is a moderated
              call: everybody arrives muted and is heard only once somebody
              running it hands them the floor. Said plainly, because an
              empty stage otherwise reads as broken audio — and the first
              thing anybody does about broken audio is leave. */}
          <p className="mt-s2 text-base text-[#b4b2a9]">
            {canGrant
              ? 'Choose somebody from People and press Allow to let them speak.'
              : 'Raise your hand and whoever is running the call can turn your microphone on.'}
          </p>
          <p className="mt-s3 text-sm text-[#888780]">
            {present === 1
              ? 'You are the only one here.'
              : `You and ${present - 1} other${present > 2 ? 's' : ''} are here.`}
          </p>
        </div>
      )}

      {listening > 0 ? (
        <p className="text-center text-sm text-[#888780]">
          {listening} listening
        </p>
      ) : null}
    </div>
  )
}

/** What the sound is doing, on the stage rather than in a banner. */
function StageNotice({
  state,
  you,
  settling,
  micDenied,
  selfMuted,
}: {
  state: AudioState
  you: CallParticipant | null
  settling: boolean
  micDenied: boolean
  selfMuted: boolean
}) {
  /* THE AIRLOCK. Somebody listening is a few seconds behind live, so the
     instant they are given the floor they are still hearing the past —
     and the first thing they do is answer a question that has already
     moved on, over whoever is talking. A short hold covers the gap. */
  if (settling) {
    return (
      <div className="rounded-lg bg-[#412402] px-s4 py-s3 text-center">
        <p className="text-base font-bold text-[#FAC775]">
          Wait for the green — you are catching up to live
        </p>
        <p className="mt-0.5 text-sm text-[#FAC775]">
          Don’t speak yet.
        </p>
      </div>
    )
  }

  if (state === 'live') {
    /* ALLOWED TO SPEAK WITH NO MICROPHONE. The one case where "everyone
       can hear you" would be a lie they act on: they talk, believe the
       room heard, and nobody did. Their connection was made to listen
       only, so coming back is what picks the microphone up. */
    if (you?.role === 'speaker' && !you.muted && micDenied) {
      return (
        <div className="rounded-lg bg-[#501313] px-s4 py-s3 text-center">
          <p className="text-base font-bold text-[#F09595]">
            You can speak, but your microphone is blocked
          </p>
          <p className="mt-0.5 text-sm text-[#F09595]">
            Allow the microphone for this site in your browser, then leave
            and join again. Nobody can hear you until you do.
          </p>
        </div>
      )
    }
    /* THEIR OWN BUTTON WINS. Telling somebody everyone can hear them
       while their microphone is off is the one message on this screen
       that must never be wrong. */
    if (you?.role === 'speaker' && !you.muted && selfMuted) {
      return (
        <div className="rounded-lg bg-[#412402] px-s4 py-s3 text-center">
          <p className="text-base font-bold text-[#FAC775]">
            You are muted — nobody can hear you
          </p>
          <p className="mt-0.5 text-sm text-[#FAC775]">
            Press Unmute when you want to speak.
          </p>
        </div>
      )
    }
    /* Being given the floor is the one change worth interrupting for. */
    if (you?.role === 'speaker' && !you.muted) {
      return (
        <div className="rounded-lg bg-[#173404] px-s4 py-s3 text-center">
          <p className="text-base font-bold text-[#C0DD97]">
            Your microphone is on — everyone can hear you
          </p>
        </div>
      )
    }
    /* LISTENING. Said once, with the way to speak in the same breath —
       somebody who cannot find how to ask assumes they are not allowed. */
    if (you?.hand_raised) {
      return (
        <div className="rounded-lg bg-[#042C53] px-s4 py-s3 text-center">
          <p className="text-base font-bold text-[#B5D4F4]">
            Your hand is up
          </p>
          <p className="mt-0.5 text-sm text-[#B5D4F4]">
            {you.queue_position
              ? `You are number ${you.queue_position} in line. `
              : ''}
            Whoever is running the call will let you speak.
          </p>
        </div>
      )
    }
    if (you) {
      return (
        <div className="rounded-lg bg-[#262626] px-s4 py-s3 text-center">
          <p className="text-base font-semibold text-[#f1efe8]">
            You are listening
          </p>
          <p className="mt-0.5 text-sm text-[#b4b2a9]">
            Raise your hand if you would like to speak.
          </p>
        </div>
      )
    }
    return null
  }

  if (micDenied) {
    return (
      <div className="rounded-lg bg-[#412402] px-s4 py-s3 text-center text-base text-[#FAC775]">
        Your browser is not letting us use the microphone. You can still hear
        the call — allow the microphone if you want to speak.
      </div>
    )
  }

  if (state === 'reconnecting') {
    return (
      <div className="rounded-lg bg-[#412402] px-s4 py-s3 text-center text-base text-[#FAC775]">
        The sound dropped — reconnecting you.
      </div>
    )
  }

  if (state === 'failed') {
    return (
      <div className="rounded-lg bg-[#412402] px-s4 py-s3 text-center text-base text-[#FAC775]">
        The sound is not answering yet — trying again.
      </div>
    )
  }

  if (state === 'unreachable') {
    return (
      <div className="rounded-lg bg-[#501313] px-s4 py-s3 text-center text-base text-[#F09595]">
        Your network blocked this connection, so you cannot hear the call. A
        different network — mobile data rather than office wifi — usually
        works.
      </div>
    )
  }

  return (
    <div className="rounded-lg bg-[#262626] px-s4 py-s3 text-center text-base text-[#b4b2a9]">
      {state === 'asking' ? 'Allow the microphone to join in.' : 'Connecting the sound…'}
    </div>
  )
}

/* -------------------------------------------------------------- toolbar */
/*
 * ALWAYS THE SAME THINGS IN THE SAME PLACE. Icon above label, the way
 * every call app people have already used works — so nobody has to learn
 * this one.
 */

function Toolbar({
  callId,
  you,
  audio,
  present,
  panelOpen,
  onTogglePanel,
  onLeave,
  leaving,
  canEnd,
  handsWaiting,
  onEnd,
  ending,
}: {
  callId: string
  you: CallParticipant
  audio: { state: AudioState; muted: boolean; toggleMute: () => void }
  present: number
  panelOpen: boolean
  onTogglePanel: () => void
  onLeave: () => void
  leaving: boolean
  canEnd: boolean
  /** Hands up, for whoever can answer them. Zero for everybody else. */
  handsWaiting: number
  onEnd: () => void
  ending: boolean
}) {
  const hand = useRaiseHand(callId)
  /* Everybody holds a seat; being heard is the seat AND not muted. */
  const speaking = you.role === 'speaker' && !you.muted
  const off = audio.muted || !speaking

  return (
    <footer className="flex shrink-0 items-center justify-center gap-s2 border-t border-[#2e2e2e] px-s4 py-s3 md:gap-s6">
      <ToolButton
        label={!speaking ? 'Mic off' : off ? 'Unmute' : 'Mute'}
        danger={speaking && off}
        disabled={!speaking}
        onClick={audio.toggleMute}
        icon={off ? MicOff : Mic}
      />

      {!speaking ? (
        <ToolButton
          label={you.hand_raised ? 'Hand up' : 'Raise hand'}
          active={you.hand_raised}
          loading={hand.isPending}
          onClick={() => hand.mutate(!you.hand_raised)}
          icon={Hand}
        />
      ) : null}

      {/* ON EVERY SCREEN. It used to be hidden on a phone, along with the
          panel, which left a moderator on a phone unable to see a hand
          or let anybody speak. */}
      <ToolButton
        label="People"
        badge={handsWaiting || present}
        alert={handsWaiting > 0}
        active={panelOpen}
        onClick={onTogglePanel}
        icon={Users}
      />

      <ToolButton
        label="Leave"
        loading={leaving}
        onClick={onLeave}
        icon={PhoneOff}
      />

      {/* ENDING IS NOT LEAVING, and only somebody running the call can do
          it. A call nobody ends stays live on the list for ever, the
          recording is never written, and the report has nothing to report
          — attendance is only complete once there is a last person out. */}
      {canEnd ? (
        <ToolButton
          label="End call"
          danger
          loading={ending}
          onClick={onEnd}
          icon={PhoneOff}
        />
      ) : null}
    </footer>
  )
}

function ToolButton({
  label,
  icon: Icon,
  onClick,
  active,
  danger,
  disabled,
  loading,
  badge,
  alert,
  className,
}: {
  label: string
  icon: typeof Mic
  onClick: () => void
  active?: boolean
  danger?: boolean
  disabled?: boolean
  loading?: boolean
  badge?: number
  /** The badge is asking for attention rather than counting. */
  alert?: boolean
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        'flex min-w-[4rem] flex-col items-center gap-1 rounded-lg px-s3 py-s2 transition-colors',
        'hover:bg-[#262626] disabled:opacity-50 disabled:hover:bg-transparent',
        active && 'bg-[#262626]',
        className,
      )}
    >
      <span className="relative">
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        ) : (
          <Icon
            className={cn('h-5 w-5', danger ? 'text-[#F09595]' : 'text-[#f1efe8]')}
            aria-hidden
          />
        )}
        {badge !== undefined ? (
          <span
            className={cn(
              'absolute -right-2 -top-1 rounded-pill px-1 text-[10px] font-bold',
              alert ? 'bg-[#EF9F27] text-[#1a1a1a]' : 'bg-[#444441]',
            )}
          >
            {badge}
          </span>
        ) : null}
      </span>
      <span className={cn('text-xs', danger ? 'text-[#F09595]' : 'text-[#b4b2a9]')}>
        {label}
      </span>
    </button>
  )
}

/** A clock that moves, because a number that moves is proof of life. */
function useElapsed(since: string | null): string | null {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  if (!since) return null
  const seconds = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000))
  const m = Math.floor(seconds / 60)
  const sec = seconds % 60
  return `${m}:${String(sec).padStart(2, '0')}`
}


/* ----------------------------------------------------------------- parts */

/* ----------------------------------------------------------------- parts */

function RoleBadge({ name }: { name: string }) {
  return (
    <span className="shrink-0 rounded-pill bg-brand-softer px-s2 py-0.5 text-xs font-semibold text-brand">
      {name}
    </span>
  )
}

/** "3m 12s", "48s" — a wait somebody can act on, not a timestamp. */
function waited(seconds: number | null): string {
  if (seconds === null) return 'a moment'
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}m ${String(s).padStart(2, '0')}s`
}


/** What to say under a speaker's name when they are not muted. */
function speakerNote(p: CallParticipant): string {
  if (p.is_moderator) return 'Leading'
  return p.role_name ?? 'Speaking'
}
